import { FlexBox } from '@dixa/lib-shared-habla';
import isPlainObject from 'lodash/isPlainObject';
import { ReactNode } from 'react';

import {
  createPrettyKeyCombinations,
  KeyboardKeyCombination,
  PrettyKeyCombinationsOptions,
  useCodeKeyMap,
} from 'utils/keyboard';

import {
  KeyboardKeyCombinationNamedVariant,
  keyboardKeyCombinationVariantConfig,
} from '../keyboardKeyCombination/keyboardKeyCombination';
import styles from '../keyboardKeyCombination/keyboardKeyCombination.module.scss';

type KeyboardKeyCombinationsProps = {
  /** The key combinations. */
  keyCombinations: readonly KeyboardKeyCombination[];
  /** The variant of displayed key combinations. */
  variant?: KeyboardKeyCombinationsVariant;
};

/** Represents a components for displaying multiple keyboard key combinations. */
export const KeyboardKeyCombinations = (
  props: KeyboardKeyCombinationsProps,
): JSX.Element => {
  const codeKeyMap = useCodeKeyMap();
  const { keyCombinations, variant = 'plain' } = props;
  const { wrapperFn, prettyKeyCombinationsOptions } = isCustomVariant(variant)
    ? variant
    : keyboardKeyCombinationsVariantConfig[variant];

  const content = createPrettyKeyCombinations(keyCombinations, {
    ...prettyKeyCombinationsOptions,
    codeKeyMap,
  });

  return <>{wrapperFn?.(content) ?? content}</>;
};

/** Represents the available keyboard key combinations variants. */
export type KeyboardKeyCombinationsVariant =
  | KeyboardKeyCombinationNamedVariant
  | KeyboardKeyCombinationsVariantConfig;

/** Represents the keyboard key combinations variant configuration. */
export type KeyboardKeyCombinationsVariantConfig = {
  /** The pretty key combinations options. */
  readonly prettyKeyCombinationsOptions: Omit<
    PrettyKeyCombinationsOptions,
    'codeKeyMap'
  >;
  /** The function for wrapping the final output. */
  readonly wrapperFn?: (content: ReactNode) => ReactNode;
};

/** Represents the variants configuration. */
export const keyboardKeyCombinationsVariantConfig: Record<
  KeyboardKeyCombinationNamedVariant,
  KeyboardKeyCombinationsVariantConfig
> = {
  plain: {
    prettyKeyCombinationsOptions: {
      ...keyboardKeyCombinationVariantConfig.plain.prettyKeyCombinationOptions,
      keyCombinationSeparator: ' or ',
      keyCombinationTransformFn: (keyCombination, index) => (
        <kbd key={index} className={styles.kbd}>
          {keyCombination}
        </kbd>
      ),
    },
    wrapperFn: keyboardKeyCombinationVariantConfig.plain.wrapperFn,
  },
  simple: {
    prettyKeyCombinationsOptions: {
      ...keyboardKeyCombinationVariantConfig.simple.prettyKeyCombinationOptions,
      keyCombinationSeparator: ' or ',
      keyCombinationTransformFn: (keyCombination, index) => (
        <FlexBox
          key={index}
          className={styles.kbd}
          as="kbd"
          gap="100"
          alignItems="center"
        >
          {keyCombination}
        </FlexBox>
      ),
    },
    wrapperFn: keyboardKeyCombinationVariantConfig.simple.wrapperFn,
  },
  rich: {
    prettyKeyCombinationsOptions: {
      ...keyboardKeyCombinationVariantConfig.rich.prettyKeyCombinationOptions,
      keyCombinationSeparator: ' or ',
      keyCombinationTransformFn: (keyCombination, index) => (
        <FlexBox
          key={index}
          className={styles.kbd}
          as="kbd"
          gap="100"
          alignItems="center"
        >
          {keyCombination}
        </FlexBox>
      ),
    },
    wrapperFn: keyboardKeyCombinationVariantConfig.rich.wrapperFn,
  },
};

/** A type guard for determining whether the variant is a custom configuration. */
function isCustomVariant(
  variant: KeyboardKeyCombinationsVariant,
): variant is KeyboardKeyCombinationsVariantConfig {
  return isPlainObject(variant);
}
